import { Injectable, EventEmitter } from '@angular/core';
import { List } from '../constants-types/PiList.model';
import { Company } from '../core-data-model/Company.model';
import { Folder } from '../core-data-model/Folder.model';
import { Group } from '../core-data-model/Group.model';
import { Job } from '../core-data-model/Job.model';
import { OiLink } from '../core-data-model/OiLink.model';
import { Person } from '../core-data-model/Person.model';
import { SearchResult } from '../core-data-model/SearchResult.model';
import { SearchResultSet } from '../core-data-model/SearchResultSet';

export enum ResultSetOrigin {
    None,
    EmployeeDirectory,
    FolderPage,
    GroupPage,
    JobsDirectory,
    PendingAssessments,
    RecentlyCompleted,
    AnalyzeGroups,
    TeamStyles,
    AssessmentCenter,
    MultipleContributorSend,
    JobPagePeopleSearch,
    PersonPage
}

@Injectable({ providedIn: 'root' })
export class ContextDataService {
    // The select folder modal which should be used as a global selector for folders
    selectedFolder: Folder;

    // Bulk update selected company
    updateSelectedCompany: Company;

    // Global selected group (i.e. on a group page)
    selectedGroup: Group;

    pendingResults: SearchResult[] = [];
    jobDashboardOverview: SearchResult[] = [];
    groupPageResults: SearchResult[] = [];
    folderPageResults: SearchResult[] = [];
    activeJobsCardResults: SearchResult[] = [];
    recentlyCompletedCardResults: SearchResult[] = [];

    assessmentLinkResults: OiLink[] = [];

    private lastResultSetName: ResultSetOrigin | string = ResultSetOrigin.None;

    job: Job;

    person: Person;

    updateJobForRecipients: Job | null = null;

    currentSet: SearchResultSet;
    savedContext: SearchResultSet;

    tableContextUpdate: EventEmitter<void> = new EventEmitter();

    constructor() {
        this.currentSet = new SearchResultSet();
    }

    _hoveredResult: SearchResult;
    get hoveredResult() {
        if (this.currentSet.size === 1) {
            return this.currentSet.first();
        }
        return this._hoveredResult;
    }

    set hoveredResult(value: SearchResult) {
        this._hoveredResult = value;
    }

    get hoveredPerson(): Person {
        if (
            this.currentSet.size === 1 &&
            this.currentSet.first() instanceof Person
        ) {
            return this.currentSet.first() as Person;
        }
        return this._hoveredResult && this._hoveredResult instanceof Person
            ? this._hoveredResult
            : null;
    }

    get selectedResultSetPeople(): List<Person> {
        return this.currentSet
            .filter((result): result is Person => result instanceof Person)
            .map((result) => result);
    }

    allResultsInContext(contents: SearchResult[]): boolean {
        return this.currentSet.allResultsInContext(contents);
    }

    isInResultSet(result: SearchResult): boolean {
        return this.currentSet.isInResultSet(result);
    }

    addToResultSet(result: SearchResult) {
        this.currentSet = this.currentSet.addToResultSet(result);
    }

    addRowSet(results: SearchResult[]) {
        this.currentSet = this.currentSet.addRowSet(results);
    }

    clearResultSet(
        resultSetName: ResultSetOrigin | string = ResultSetOrigin.None
    ) {
        if (
            resultSetName === ResultSetOrigin.None ||
            resultSetName !== this.lastResultSetName
        ) {
            this.currentSet = new SearchResultSet();
        }
        this.lastResultSetName = resultSetName;
    }

    removeFromResultSet(result: SearchResult) {
        this.currentSet = this.currentSet.removeFromResultSet(result);
    }

    filterAndUpdateResultSet(
        filterFunction: (item: SearchResult) => boolean
    ): List<SearchResult> {
        this.currentSet
            .filter((item): item is SearchResult => !filterFunction(item))
            .forEach((item) => {
                this.removeFromResultSet(item);
            });
        return this.currentSet;
    }

    updateJobForPersons(newJob: Job) {
        if (newJob) {
            this.currentSet
                .filter((result): result is Person => result instanceof Person)
                .forEach((result) => (result.job = newJob));
        }
    }

    removeRowSet(results: SearchResult[]) {
        this.currentSet = this.currentSet.removeRowSet(results);
    }

    toggleFromResultSet(result: SearchResult) {
        this.currentSet = this.currentSet.toggleFromResultSet(result);
        return this.currentSet;
    }

    get resultSetPersonWithPatternCount() {
        return this.currentSet.resultSetPersonWithPatternCount;
    }
    get resultSetHasPerson() {
        return this.currentSet.resultSetHasPerson;
    }
    get resultSetJobWithTargetCount() {
        return this.currentSet.resultSetJobWithTargetCount;
    }
    get resultSetHasJob() {
        return this.currentSet.resultSetHasJob;
    }

    noResultsInContext(contents: SearchResult[]): boolean {
        return this.currentSet.noResultsInContext(contents);
    }

    // this is a stop gap until I have more time to improve the notion
    // of context
    storeContextForSwapping() {
        this.savedContext = this.currentSet.makeCopy();
    }

    restoreSwappedContext() {
        this.currentSet = this.savedContext;
    }
}
